<script>
  import { useRoute } from '@dvcol/svelte-simple-router';
  import api from '$lib/api.js';
  import { authStore } from '../stores/authStore.svelte.js';
  import PageHeader from '../components/PageHeader.svelte';
  import EmptyState from '../components/EmptyState.svelte';

  // Route params must come from useRoute(), not $props() — plugins/augurly_admin's
  // claude.md documents that RouterView often passes them through as undefined.
  const { route, location } = useRoute();
  const propertyId = $derived(Number(location?.params?.id ?? route?.params?.id));

  let loading = $state(true);
  let error = $state(null);
  let property = $state(null);
  let knowledge = $state([]);

  $effect(() => {
    if (!propertyId) return;
    loading = true;
    error = null;
    (async () => {
      try {
        const params = { tenant_id: authStore.tenantId ?? undefined };
        const [p, k] = await Promise.all([
          api.getProperty(propertyId, params),
          api.getPropertyKnowledge(propertyId, params),
        ]);
        property = p;
        knowledge = k;
      } catch (e) {
        error = e.status === 404
          ? 'This property is not available for your account.'
          : e.message;
      } finally {
        loading = false;
      }
    })();
  });

  const grouped = $derived.by(() => {
    const map = new Map();
    for (const item of knowledge) {
      if (!map.has(item.knowledge_type)) map.set(item.knowledge_type, []);
      map.get(item.knowledge_type).push(item);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  });

  const sourceLabel = (s) =>
    s === 'tally_form' ? 'Owner form' : s === 'hostaway_listing' ? 'Hostaway listing' : (s ?? 'Other');
</script>

{#if error}
  <p class="p-card p-4 text-sm" style="color: var(--p-danger)">{error}</p>
{:else if loading}
  <p class="p-muted text-sm">Loading…</p>
{:else if property}
  <PageHeader title={property.name}
              subtitle={[property.address, property.city, property.country].filter(Boolean).join(', ')} />

  <div class="p-card mb-6 grid grid-cols-2 gap-4 p-4 text-sm sm:grid-cols-4">
    <div><p class="text-xs p-muted">Bedrooms</p><p class="mt-1">{property.bedrooms ?? '—'}</p></div>
    <div><p class="text-xs p-muted">Bathrooms</p><p class="mt-1">{property.bathrooms ?? '—'}</p></div>
    <div><p class="text-xs p-muted">Sleeps</p><p class="mt-1">{property.accommodates ?? '—'}</p></div>
    <div>
      <p class="text-xs p-muted">Knowledge items</p>
      <p class="mt-1">{property.knowledge_item_count}</p>
    </div>
  </div>

  <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
    <h2 class="text-sm font-semibold">What the assistant knows</h2>
    <p class="text-xs p-faint">Read-only — see below</p>
  </div>

  <p class="mb-4 rounded-lg px-3 py-2 text-xs"
     style="background-color: var(--p-info-bg); color: var(--p-info)">
    <i class="fas fa-circle-info mr-1" aria-hidden="true"></i>
    These facts are generated from your Hostaway listing and your property-information form.
    To correct anything here, update the form — changes sync back automatically. Editing in the
    portal would be overwritten on the next sync.
  </p>

  {#if !knowledge.length}
    <EmptyState icon="fa-book" title="No knowledge recorded yet"
                message="Once your property-information form is submitted and matched, its facts appear here." />
  {:else}
    {#each grouped as [type, items]}
      <section class="mb-6">
        <h3 class="mb-2 text-xs font-semibold uppercase tracking-wide p-muted">{type}</h3>
        <div class="space-y-2">
          {#each items as item}
            <article class="p-card p-4">
              <div class="flex flex-wrap items-start justify-between gap-2">
                <h4 class="text-sm font-medium">{item.title}</h4>
                <div class="flex items-center gap-2">
                  <span class="p-badge" style="background-color: var(--p-bg-inset); color: var(--p-text-muted)">
                    {sourceLabel(item.source_type)}
                  </span>
                  {#if !item.is_vectorized}
                    <span class="p-badge" style="background-color: var(--p-warning-bg); color: var(--p-warning)"
                          title="Not yet searchable by the assistant">Pending</span>
                  {/if}
                </div>
              </div>
              <p class="mt-2 whitespace-pre-wrap text-sm p-muted">{item.content}</p>
            </article>
          {/each}
        </div>
      </section>
    {/each}
  {/if}
{/if}
